import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import type { AdminGuideListItem } from '@/features/admin/api/admin.api';
import { GuideActionsMenu } from './GuideActionsMenu';
import {
  ExperienceValue,
  GuideIdentityCell,
  GuideStatusBadge,
  RatingValue,
  VisibilityPill,
} from './GuideDirectoryCells';
import { formatDate, formatLocation } from './guideDirectory.utils';

export interface GuideRowHandlers {
  canDelete: boolean;
  onView: (guide: AdminGuideListItem) => void;
  onOpenIdentityCard: (guide: AdminGuideListItem) => void;
  onCopy: (value: string, label: string) => void;
  onDelete: (guide: AdminGuideListItem) => void;
}

interface GuideDirectoryListProps extends GuideRowHandlers {
  guides: AdminGuideListItem[];
  isLoading: boolean;
  skeletonRows: number;
  emptyState: ReactNode;
}

const COLUMNS = [
  { key: 'guide', label: 'Guide', width: '24%' },
  { key: 'contact', label: 'Contact', width: '20%' },
  { key: 'location', label: 'Location', width: '13%' },
  { key: 'experience', label: 'Experience' },
  { key: 'rating', label: 'Rating' },
  { key: 'status', label: 'Status' },
  { key: 'visibility', label: 'Visibility' },
  { key: 'updated', label: 'Updated' },
  { key: 'actions', label: '', width: 64, align: 'right' as const },
];

const headCellSx = {
  py: 1.25,
  fontSize: '0.75rem',
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'text.secondary',
  bgcolor: 'var(--border-light)',
  borderBottom: '1px solid',
  borderColor: 'divider',
  whiteSpace: 'nowrap',
} as const;

const bodyCellSx = {
  py: 1.5,
  borderBottom: '1px solid',
  borderColor: 'var(--border-light)',
  verticalAlign: 'middle',
} as const;

function ContactValue({ guide }: { guide: AdminGuideListItem }) {
  const { phone, email } = guide.user ?? {};
  if (!phone && !email) {
    return (
      <Typography variant="body2" sx={{ color: 'textColors.muted' }}>
        —
      </Typography>
    );
  }
  return (
    <Box sx={{ minWidth: 0 }}>
      {phone && (
        <Typography variant="body2" noWrap sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {phone}
        </Typography>
      )}
      {email && (
        <Typography
          variant={phone ? 'caption' : 'body2'}
          color={phone ? 'text.secondary' : 'text.primary'}
          noWrap
          title={email}
          sx={{ display: 'block' }}
        >
          {email}
        </Typography>
      )}
    </Box>
  );
}

/** The row itself opens the profile; everything else lives in the ⋯ menu. */
function RowActions({ guide, ...handlers }: GuideRowHandlers & { guide: AdminGuideListItem }) {
  return (
    <GuideActionsMenu
      guide={guide}
      canDelete={handlers.canDelete}
      onView={() => handlers.onView(guide)}
      onOpenIdentityCard={() => handlers.onOpenIdentityCard(guide)}
      onCopy={handlers.onCopy}
      onDelete={() => handlers.onDelete(guide)}
    />
  );
}

function DesktopTable({ guides, isLoading, skeletonRows, emptyState, ...handlers }: GuideDirectoryListProps) {
  return (
    <TableContainer>
      <Table sx={{ tableLayout: 'fixed', minWidth: 1080 }}>
        <TableHead>
          <TableRow>
            {COLUMNS.map((column) => (
              <TableCell key={column.key} align={column.align} sx={{ ...headCellSx, width: column.width }}>
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            Array.from({ length: skeletonRows }, (_, index) => (
              <TableRow key={index}>
                {COLUMNS.map((column) => (
                  <TableCell key={column.key} sx={bodyCellSx}>
                    {column.key === 'guide' ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Skeleton variant="circular" width={36} height={36} />
                        <Box sx={{ flex: 1 }}>
                          <Skeleton width="70%" />
                          <Skeleton width="45%" />
                        </Box>
                      </Box>
                    ) : (
                      <Skeleton width={column.key === 'actions' ? 32 : '70%'} sx={{ ml: column.align ? 'auto' : 0 }} />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : guides.length === 0 ? (
            <TableRow>
              <TableCell colSpan={COLUMNS.length} sx={{ border: 0 }}>
                {emptyState}
              </TableCell>
            </TableRow>
          ) : (
            guides.map((guide) => (
              <TableRow
                key={guide.id}
                hover
                onClick={() => handlers.onView(guide)}
                sx={{
                  cursor: 'pointer',
                  '&:last-of-type td': { borderBottom: 0 },
                  '&.MuiTableRow-hover:hover': { bgcolor: 'var(--border-light)' },
                }}
              >
                <TableCell sx={bodyCellSx}>
                  <GuideIdentityCell guide={guide} />
                </TableCell>
                <TableCell sx={bodyCellSx}>
                  <ContactValue guide={guide} />
                </TableCell>
                <TableCell sx={bodyCellSx}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ color: formatLocation(guide) ? 'text.primary' : 'textColors.muted' }}
                  >
                    {formatLocation(guide) ?? '—'}
                  </Typography>
                </TableCell>
                <TableCell sx={bodyCellSx}>
                  <ExperienceValue years={guide.yearsOfExperience} />
                </TableCell>
                <TableCell sx={bodyCellSx}>
                  <RatingValue rating={guide.rating} reviewCount={guide.reviewCount} />
                </TableCell>
                <TableCell sx={bodyCellSx}>
                  <GuideStatusBadge status={guide.status} />
                </TableCell>
                <TableCell sx={bodyCellSx}>
                  <VisibilityPill live={guide.isVisible} />
                </TableCell>
                <TableCell sx={bodyCellSx}>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {formatDate(guide.updatedAt)}
                  </Typography>
                </TableCell>
                <TableCell sx={bodyCellSx} align="right">
                  <RowActions guide={guide} {...handlers} />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function MobileCards({ guides, isLoading, skeletonRows, emptyState, ...handlers }: GuideDirectoryListProps) {
  if (isLoading) {
    return (
      <Stack divider={<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />}>
        {Array.from({ length: Math.min(skeletonRows, 6) }, (_, index) => (
          <Box key={index} sx={{ p: 2, display: 'flex', gap: 1.5 }}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box sx={{ flex: 1 }}>
              <Skeleton width="60%" />
              <Skeleton width="40%" />
              <Skeleton width="80%" />
            </Box>
          </Box>
        ))}
      </Stack>
    );
  }
  if (guides.length === 0) return <>{emptyState}</>;

  return (
    <Stack divider={<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />}>
      {guides.map((guide) => {
        const location = formatLocation(guide);
        return (
          <Box
            key={guide.id}
            role="button"
            tabIndex={0}
            onClick={() => handlers.onView(guide)}
            onKeyDown={(event) => {
              if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                handlers.onView(guide);
              }
            }}
            sx={{
              p: 2,
              cursor: 'pointer',
              '&:hover, &:focus-visible': { bgcolor: 'var(--border-light)', outline: 'none' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <GuideIdentityCell guide={guide} dense />
              </Box>
              <GuideStatusBadge status={guide.status} />
            </Box>

            <Box
              sx={{
                mt: 1.5,
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) auto',
                columnGap: 2,
                rowGap: 0.75,
                alignItems: 'center',
              }}
            >
              <ContactValue guide={guide} />
              <VisibilityPill live={guide.isVisible} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, color: 'text.secondary' }}>
                <PlaceOutlinedIcon sx={{ fontSize: 16 }} aria-hidden />
                <Typography variant="caption" noWrap>
                  {location ?? 'No location'}
                  {guide.yearsOfExperience != null ? ` · ${guide.yearsOfExperience} yrs` : ''}
                </Typography>
              </Box>
              <RatingValue rating={guide.rating} reviewCount={guide.reviewCount} />
            </Box>

            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Updated {formatDate(guide.updatedAt)}
              </Typography>
              <RowActions guide={guide} {...handlers} />
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

export function GuideDirectoryList(props: GuideDirectoryListProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  return isDesktop ? <DesktopTable {...props} /> : <MobileCards {...props} />;
}
